import type {
  EmbeddedIframeMessage,
  EmbeddedPortalMessage,
} from '@/types/embeddedApplication';

type MessageHandler = (message: EmbeddedIframeMessage) => void;

interface EmbeddedMessageContext {
  origin: string;
  source: Window;
}

type MessageHandlerWithContext = (
  message: EmbeddedIframeMessage,
  context: EmbeddedMessageContext,
) => void;

interface IframeRegistration {
  iframeWindow: Window;
  trustedOrigin: string;
}

const embeddedAuthMessageTypes = new Set<string>([
  'embeddedAuthRequired',
  'embeddedAuthRefreshRequired',
  'embeddedAuthLogout',
]);

const isEmbeddedAuthMessageType = (type: string | undefined): boolean => {
  return typeof type === 'string' && embeddedAuthMessageTypes.has(type);
};

const isWindowLikeMessageSource = (
  source: MessageEventSource | null,
): source is Window => {
  if (!source || typeof source !== 'object') {
    return false;
  }

  return typeof (source as Window).postMessage === 'function';
};

class EmbeddedCommunicationService {
  private iframeWindow: Window | null = null;

  private activeTrustedOrigin: string | null = null;

  private iframeRegistrations = new WeakMap<Window, IframeRegistration>();

  private registeredIframes: IframeRegistration[] = [];

  private inboundHandlers = new Set<MessageHandler>();

  private inboundHandlersWithContext = new Set<MessageHandlerWithContext>();

  bindIframeWindow(windowRef: Window | null): void {
    this.iframeWindow = windowRef;

    if (!windowRef) {
      this.activeTrustedOrigin = null;
      return;
    }

    const registration = this.iframeRegistrations.get(windowRef);
    this.activeTrustedOrigin = registration?.trustedOrigin ?? null;
  }

  registerIframe(iframeWindow: Window | null, trustedOrigin: string): void {
    if (!iframeWindow) {
      return;
    }

    const registration = {
      iframeWindow,
      trustedOrigin,
    };

    this.iframeRegistrations.set(iframeWindow, registration);
    this.registeredIframes = this.registeredIframes.filter(
      (entry) => entry.iframeWindow !== iframeWindow,
    );
    this.registeredIframes.push(registration);

    if (this.iframeWindow === iframeWindow) {
      this.activeTrustedOrigin = trustedOrigin;
    }
  }

  unregisterIframe(iframeWindow: Window | null): void {
    if (!iframeWindow) {
      return;
    }

    this.registeredIframes = this.registeredIframes.filter(
      (entry) => entry.iframeWindow !== iframeWindow,
    );
  }

  broadcast(message: EmbeddedPortalMessage): void {
    this.registeredIframes.forEach(({ iframeWindow, trustedOrigin }) => {
      iframeWindow.postMessage(message, trustedOrigin);
    });
  }

  sendToIframe(message: EmbeddedPortalMessage): void {
    if (!this.iframeWindow || !this.activeTrustedOrigin) {
      return;
    }

    this.iframeWindow.postMessage(message, this.activeTrustedOrigin);
  }

  sendToIframeWithOrigin(
    iframeWindow: Window,
    trustedOrigin: string,
    message: EmbeddedPortalMessage,
  ): void {
    iframeWindow.postMessage(message, trustedOrigin);
  }

  subscribe(handler: MessageHandler): () => void {
    this.inboundHandlers.add(handler);

    return () => {
      this.inboundHandlers.delete(handler);
    };
  }

  subscribeWithContext(handler: MessageHandlerWithContext): () => void {
    this.inboundHandlersWithContext.add(handler);

    return () => {
      this.inboundHandlersWithContext.delete(handler);
    };
  }

  initialize(): () => void {
    const handleMessage = (event: MessageEvent<EmbeddedIframeMessage>) => {
      if (!event.data?.type) {
        return;
      }

      if (!event.source || typeof event.origin !== 'string') {
        return;
      }

      if (!isWindowLikeMessageSource(event.source)) {
        return;
      }

      const sourceWindow = event.source;
      const registration = this.iframeRegistrations.get(sourceWindow);
      const isEmbeddedAuthMessage = isEmbeddedAuthMessageType(event.data.type);

      if (!registration) {
        if (!isEmbeddedAuthMessage) {
          return;
        }
      } else if (registration.trustedOrigin !== event.origin) {
        return;
      }

      this.inboundHandlers.forEach((handler) => {
        handler(event.data);
      });

      this.inboundHandlersWithContext.forEach((handler) => {
        handler(event.data, {
          origin: event.origin,
          source: sourceWindow,
        });
      });
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }
}

export const embeddedCommunicationService = new EmbeddedCommunicationService();
